import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, filter, map } from 'rxjs/operators';
import { RouterOutlet } from '@angular/router';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { Store } from '@ngxs/store';
import { v4 as uuidv4 } from 'uuid';
import {
  DATA_SELECTOR_INJECTION_TOKEN,
  DmsIntegrationStateSelectors,
  EntityType,
  Logger,
  SharedModule,
  TrackService,
  UploadDocument,
  UploadFileJob,
  UploadFileStatus,
  UploadType,
} from '@aiwyn/shared';
import { UploadToastComponent } from '../../shared/components/upload-toast/upload-toast.component';
import { AppState } from '../../../../state/app.state';
import { CxpTasksDataStateService } from '../../services/cxp-tasks-data-state.service';
import { CxpTasksPermissionsService } from '../../services/cxp-tasks-permissions.service';
import { CxpCrlFiltersService } from '../../services/cxp-crl-filters.service';
import { CxpRouteParamsService } from '../../services/cxp-route-params.service';
import {
  applyCrlFilters,
  buildCrlFilterOptions,
  buildCrlSectionOptions,
  isCrlPresetActive,
  toggleCrlFilter,
  toggleCrlPreset,
} from '../../services/utils/cxp-crl-filters.utils';
import { CxpCrlDetailSelectors } from '../../state/cxp-crl-detail-state.selectors';
import { LoadRequestDetail, SetSelectedItemId, SetSelectedRequestId, UndoItemNotApplicable } from '../../state/cxp-crl-detail.actions';
import { CxpCrlSubscriptionsStateSelectors } from '../../state/cxp-crl-subscriptions-state.selectors';
import {
  RemoveCategorizationFile,
  StartCrlSubscriptions,
  StopCrlSubscriptions,
} from '../../state/cxp-crl-subscriptions.actions';
import {
  CrlBulkAction,
  CrlCategorizationFile,
  CrlFilterKey,
  CrlFilterSelection,
  CRL_ACTION_REQUIRED_FILTERS,
  CrlRequestItem,
  CrlSectionFilter,
  isCrlItemBulkSelectable,
} from '../../models';
import { isItemScopedCrlUpload } from '../../shared/utils/crl-upload-scope.util';
import { CxpCrlPanelComponent } from '../cxp-crl-panel/cxp-crl-panel.component';
import { CRL_PANEL_NAVIGATOR, HostedCrlPanelNavigator, routedCrlPanelNavigator } from '../../services/crl-panel-navigator';
import { CrlPageHeaderComponent } from './components/crl-page-header/crl-page-header.component';
import { CrlProgressTilesComponent } from './components/crl-progress-tiles/crl-progress-tiles.component';
import { CrlActionBannerComponent } from './components/crl-action-banner/crl-action-banner.component';
import { CrlBulkDropzoneComponent } from './components/crl-bulk-dropzone/crl-bulk-dropzone.component';
import { CrlUncategorizedFilesCardComponent } from './components/crl-uncategorized-files-card/crl-uncategorized-files-card.component';
import { CrlBulkActionBarComponent } from './components/crl-bulk-action-bar/crl-bulk-action-bar.component';
import { CrlSearchFilterBarComponent } from './components/crl-search-filter-bar/crl-search-filter-bar.component';
import { CrlSectionListComponent } from './components/crl-section-list/crl-section-list.component';
import { CrlRightRailComponent } from './components/crl-right-rail/crl-right-rail.component';

/**
 * How long completions are gathered before one reload answers for all of them.
 * Sized against the shared uploader's five-at-a-time concurrency, so a batch
 * finishing together costs one `/page` read rather than five.
 */
const UPLOAD_RECONCILE_DEBOUNCE_MS = 300;

/**
 * Redesigned CRL list-view page (PP-3752) — the flag-gated replacement for
 * TaskRequestDetailContainerComponent (CXP_ENABLE_REQUEST_LIST_REDESIGN; see
 * cxp-new-crl-page.guard).
 *
 * Owns the `request/:requestId` route. The per-item detail panel (PP-3753) is a
 * CHILD route rendered into this page's named `panel` outlet, so it sits beside
 * the list on desktop.
 *
 * It also renders HOSTED inside the secure-link shell, which has no route: the
 * host passes `hostedRequestId` in place of the route param and provides a panel
 * navigator. Everything below the container is identical in both modes.
 *
 * Renders from one composed selector
 * (CxpCrlDetailSelectors.getSelectedRequestDisplay) that falls back to the raw
 * task-list request, so the header and tiles render immediately and upgrade in
 * place once the full detail resolves.
 */
@Component({
  selector: 'cxp-new-crl-page',
  templateUrl: './cxp-new-crl-page.component.html',
  styleUrls: ['./cxp-new-crl-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SharedModule,
    RouterOutlet,
    CxpCrlPanelComponent,
    MatProgressBarModule,
    CrlPageHeaderComponent,
    CrlProgressTilesComponent,
    CrlActionBannerComponent,
    CrlBulkDropzoneComponent,
    CrlUncategorizedFilesCardComponent,
    CrlBulkActionBarComponent,
    CrlSearchFilterBarComponent,
    CrlSectionListComponent,
    CrlRightRailComponent,
    UploadToastComponent,
  ],
  providers: [
    {
      provide: DATA_SELECTOR_INJECTION_TOKEN,
      useValue: 'cxp-new-crl-page',
    },
  ],
})
export class CxpNewCrlPageComponent implements OnInit {
  /**
   * Set by a host that renders this page without a route. It replaces the
   * `:requestId` param, and the item panel renders inline rather than through
   * the named `panel` outlet.
   */
  readonly hostedRequestId = input<number | null>(null);

  /** The breadcrumb links to /main/tasks, which a secure-link guest cannot reach. */
  readonly showBreadcrumb = input<boolean>(true);

  private readonly store = inject(Store);
  private readonly dataState = inject(CxpTasksDataStateService);
  private readonly permissionsService = inject(CxpTasksPermissionsService);
  private readonly filtersService = inject(CxpCrlFiltersService);
  private readonly routeParams = inject(CxpRouteParamsService);
  private readonly trackService = inject(TrackService);
  private readonly logger = inject(Logger);
  private readonly destroyRef = inject(DestroyRef);
  // A host provides the hosted navigator; on its own route there is none.
  private readonly panelNavigator = inject(CRL_PANEL_NAVIGATOR, { optional: true }) ?? routedCrlPanelNavigator();

  protected readonly dataSelector = inject(DATA_SELECTOR_INJECTION_TOKEN);

  /** True when a host supplied the request id instead of the router. */
  protected readonly isHosted = computed(() => this.hostedRequestId() != null);

  /**
   * The request-level file surfaces, withheld from a secure-link guest whose link
   * grants specific items rather than the request. Two separate permissions: one
   * is about adding files, the other about seeing them, and neither implies the
   * other.
   */
  protected readonly canBulkUpload = computed(() => this.permissionsService.permissions().canBulkUpload);
  protected readonly canViewUncategorizedFiles = computed(() => this.permissionsService.permissions().canViewUncategorizedFiles);

  /** The request being shown, from the host when hosted and the route otherwise. */
  private readonly activeRequestId = computed(() => this.hostedRequestId() ?? this.routeParams.requestId());

  /** Null when routed: there the outlet owns the selection, not the navigator. */
  protected readonly hostedSelectedItemId = computed(() =>
    this.panelNavigator instanceof HostedCrlPanelNavigator ? this.panelNavigator.selectedItemId() : null,
  );

  protected readonly display = toSignal(this.store.select(CxpCrlDetailSelectors.getSelectedRequestDisplay));

  /** Live view model for the uncategorized-files card below the bulk dropzone. */
  protected readonly uncategorizedCardVm = this.store.selectSignal(
    CxpCrlSubscriptionsStateSelectors.getUncategorizedCardVm,
  );

  /** True while the detail API loads — drives the top progress bar (no displacement). */
  protected readonly isLoading = computed(() => this.display()?.isLoading ?? false);

  // --- Filter + search UI state (REQ-12) -------------------------------------
  // Held as container signals (mirrors tasks-container) and applied to the
  // selector's `displaySections` via a pure helper — the selector keeps owning
  // the open/finished split + completion ordering; this only narrows the list.
  // Selection is seeded from + persisted to localStorage by the filters service.

  /** Active filter buckets, seeded from the persisted (version-guarded) selection. */
  protected readonly selection = signal<CrlFilterSelection>(this.filtersService.loadSelection());
  /** Live search input value. */
  protected readonly searchQuery = signal<string>('');
  /** Scoped section id, or null for "All Sections" (PP-3642). Session-only UI state. */
  protected readonly sectionFilter = signal<CrlSectionFilter>(null);
  /** Whether bulk-actions selection mode is active (REQ-11). */
  protected readonly bulkMode = signal(false);
  /** Ids of rows selected in bulk mode. Cleared whenever the mode is exited. */
  protected readonly selectedItemIds = signal<ReadonlySet<number>>(new Set<number>());
  /** Items with an undo in flight. The overlay reads the same slice as a single `isUndoPending`; a list needs the set. */
  private readonly pendingUndoItemIdsList = this.store.selectSignal(CxpCrlDetailSelectors.slices.pendingUndoItemIds);
  protected readonly undoPendingItemIds = computed<ReadonlySet<number>>(() => new Set(this.pendingUndoItemIdsList() ?? []));
  /**
   * Viewer's contact ids for the "Assigned to Me" bucket — the same
   * `AppState.getUserContactIds` source the detail bridge uses. Reactive
   * (selectSignal) so the bucket fills in when ids load after a direct page hit.
   */
  private readonly viewerContactIdsList = this.store.selectSignal(AppState.getUserContactIds);
  private readonly viewerContactIds = computed<ReadonlySet<number>>(() => new Set(this.viewerContactIdsList() ?? []));

  /**
   * All sections from the selector (full set) — tiles/progress summarize this.
   * Read through the cross-state selector so each row carries its live comment
   * state alongside its `/page` fields.
   */
  private readonly allSections = this.store.selectSignal(
    CxpCrlSubscriptionsStateSelectors.getDisplaySectionsWithComments,
  );

  /** Sections after applying the section scope + active filters + search (drives the list). */
  protected readonly filteredSections = computed(() =>
    applyCrlFilters(this.allSections(), this.selection(), this.searchQuery(), this.viewerContactIds(), this.sectionFilter()),
  );

  /** The seven filter options with full-request counts + selected flags. */
  protected readonly filterOptions = computed(() =>
    buildCrlFilterOptions(this.allSections(), this.selection(), this.viewerContactIds()),
  );

  /** "All Sections" dropdown options derived from the full section set (PP-3642). */
  protected readonly sectionOptions = computed(() => buildCrlSectionOptions(this.allSections()));

  /** Derived here rather than in the banner so the banner stays presentational. */
  protected readonly isActionFilterActive = computed(() => isCrlPresetActive(this.selection(), CRL_ACTION_REQUIRED_FILTERS));

  /** Count of rows selected in bulk mode — drives the batch toolbar. */
  protected readonly selectedCount = computed(() => this.selectedItemIds().size);

  /** Convenience: name shown in the header (raw-request fallback included). */
  protected readonly name = computed(() => this.display()?.name ?? '');
  /** Whether any sections survive the current filter/search (drives empty vs list). */
  protected readonly hasSections = computed(() => this.filteredSections().length > 0);
  /** True when filters/search are active but nothing matches (vs nothing loaded). */
  protected readonly isFilteredEmpty = computed(
    () => this.allSections().length > 0 && this.filteredSections().length === 0,
  );

  /** Reactive view of all DMS upload records; upload completions arrive through it. */
  private readonly allUploadJobs = this.store.selectSignal(DmsIntegrationStateSelectors.getUploadFileJobs);

  /**
   * Job ids of finished uploads bound to an item of THIS request — the ones whose
   * file must now be showing on that item. A list-level bulk drop names no item
   * (the backend assigns it later) and belongs to the categorization subscription
   * instead, so it is not counted here.
   */
  private readonly completedItemUploadIds = computed<string[]>(() => {
    const requestId = this.activeRequestId();
    if (requestId == null) return [];
    return this.allUploadJobs()
      .filter((job) => job.status === UploadFileStatus.LOADED && isItemScopedCrlUpload(job.entityData, requestId))
      .map((job) => job.jobId);
  });

  /**
   * Uploads already reconciled by a reload. Records outlive their reload — they
   * sit in DMS state until the toast is dismissed — so without this, every later
   * completion would replay the whole finished set.
   */
  private readonly reconciledUploadIds = new Set<string>();

  // Branches on an input, so it cannot run in the constructor. A hosted page has
  // no task list behind it and no legacy detail surface to keep warm, and
  // `/cxp/tasks/list` is refused for a secure-link caller.
  ngOnInit(): void {
    if (this.isHosted()) return;
    this.dataState.backgroundEffects$().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  constructor() {
    // Keyed off the REQUEST ID only: the item overlay is an aux route, so opening
    // an item never churns the socket, while navigating to a different CRL
    // restarts it. `StartCrlSubscriptions` ignores a same-id re-dispatch.
    toObservable(this.activeRequestId)
      .pipe(
        filter((requestId): requestId is number => requestId != null),
        distinctUntilChanged(),
        takeUntilDestroyed(),
      )
      .subscribe({
        next: (requestId) => {
          if (this.isHosted()) {
            this.store.dispatch([new SetSelectedRequestId(requestId), new LoadRequestDetail(requestId)]);
          }
          this.store.dispatch(new StartCrlSubscriptions(requestId));
        },
        error: (err) => this.logger.error('[CxpNewCrlPage] subscription start failed:', err),
      });

    // Surface the files finished uploads have created, wherever the drop came
    // from. Watched here rather than by the dropzone that started the upload:
    // that dropzone lives in the item overlay and is destroyed as soon as the
    // client closes it, steps onto a questionnaire item, or marks the item
    // finished, while the upload carries on in shared DMS state. The page
    // outlives all of that, so every upload ends with the request reloaded.
    toObservable(this.completedItemUploadIds)
      .pipe(
        map((completedJobIds) => this.takeUnreconciled(completedJobIds)),
        filter((jobIds) => jobIds.length > 0),
        // A batch arrives as up to five near-simultaneous completions; one reload
        // settles the lot.
        debounceTime(UPLOAD_RECONCILE_DEBOUNCE_MS),
        takeUntilDestroyed(),
      )
      .subscribe((jobIds) => {
        jobIds.forEach((jobId) => this.reconciledUploadIds.add(jobId));
        const requestId = this.activeRequestId();
        if (requestId != null) {
          this.store.dispatch(new LoadRequestDetail(requestId));
        }
      });

    // The route-params service clears the selection on navigation too, but
    // that dispatch lands on `NavigationEnd`, which fires AFTER this
    // component (for the new route) is already constructed. Clearing here as
    // well guarantees no stale `selectedRequestId`/`selectedItemId` survives
    // between this instance being destroyed and the next one reading state.
    this.destroyRef.onDestroy(() => {
      this.store.dispatch([new SetSelectedRequestId(null), new SetSelectedItemId(null), new StopCrlSubscriptions()]);
    });
  }

  /**
   * The finished uploads not yet answered by a reload, forgetting any that have
   * since left the finished set. An upload leaves it by being retried after a
   * failure, or by the toast being dismissed — the first has to reconcile again
   * when it succeeds, so it must not stay marked.
   */
  private takeUnreconciled(completedJobIds: string[]): string[] {
    const completed = new Set(completedJobIds);
    for (const jobId of this.reconciledUploadIds) {
      if (!completed.has(jobId)) this.reconciledUploadIds.delete(jobId);
    }
    return completedJobIds.filter((jobId) => !this.reconciledUploadIds.has(jobId));
  }

  /**
   * Uncategorized-card remove "X" — optimistically drops the file (handled in the
   * subscriptions state) and fires the soft/DMS delete via the bridge. Keys on the
   * FILE id; the subscription reconciles.
   */
  onRemoveUncategorized(file: CrlCategorizationFile): void {
    this.store.dispatch(new RemoveCategorizationFile(file.fileId));
    this.trackService.track('cxp_crl_uncategorized_file_removed', {
      requestId: this.display()?.requestId ?? 0,
      fileId: file.fileId,
    });
  }

  /**
   * Bulk dropzone → real DMS upload (REQ-5). Builds one queued job per accepted
   * file and dispatches `UploadDocument`. Uses the single-call
   * CLIENT_REQUEST_SUBMISSION context (the `submission-file-stream` endpoint):
   * the backend uploads the bytes AND links them in one request. Because this is
   * a LIST-level drop (not scoped to one item), `requestItemIds` is empty and
   * `skipCategorization` is false, so the backend AI categorizes each file and
   * assigns it to the right item. The upload toast tracks the batch; per-file
   * progress and cancel are owned by the DMS state + toast.
   */
  onFilesSelected(files: File[]): void {
    // Defense in depth: the dropzone is not rendered without the permission, but
    // an upload aimed at the request as a whole must never start for a caller who
    // only holds specific items.
    if (!this.canBulkUpload()) return;

    const requestId = this.display()?.requestId;
    if (!requestId || files.length === 0) return;

    const jobs: UploadFileJob[] = files.map((file) => ({
      jobId: uuidv4(),
      status: UploadFileStatus.DEFAULT,
      data: { file, fileName: file.name },
      entityData: {
        uploadType: UploadType.CLIENT_REQUEST_SUBMISSION,
        entityId: requestId,
        entityIdType: EntityType.JOB,
        clientRequestId: requestId,
        requestItemIds: [],
        skipCategorization: false,
      },
    }));

    this.store.dispatch(new UploadDocument(jobs));
    this.trackService.track('CXP CRL Bulk Upload Started', { requestId, fileCount: files.length });
  }

  /** Opens the per-item detail panel; the list keeps rendering underneath. */
  onItemSelected(item: CrlRequestItem): void {
    this.trackService.track('CXP CRL Item Selected', {
      requestId: this.display()?.requestId ?? 0,
      itemId: item.id,
      status: item.status,
    });
    this.panelNavigator.openItem(item.id);
  }

  /** The row's comment control — same panel, opened on the thread. */
  onItemCommentsSelected(item: CrlRequestItem): void {
    this.trackService.track('CXP CRL Item Comments Opened', {
      requestId: this.display()?.requestId ?? 0,
      itemId: item.id,
    });
    this.panelNavigator.openItem(item.id, 'comments');
  }

  /** A row's marked-as-N/A "Undo" → the item returns to its open state. */
  onItemNotApplicableUndone(item: CrlRequestItem): void {
    this.trackService.track('CXP CRL Item Not Applicable Undone', {
      requestId: this.display()?.requestId ?? 0,
      itemId: item.id,
    });
    this.store.dispatch(new UndoItemNotApplicable(item.id));
  }

  /**
   * Toggle one filter bucket on/off; apply immediately and persist. Selecting a
   * bucket drops the narrower ones it contains, so Waiting for You replaces
   * Overdue rather than sitting beside it describing the same list.
   */
  onFilterToggled(key: CrlFilterKey): void {
    const next = toggleCrlFilter(this.selection(), key);
    this.commitSelection(next);
    this.trackService.track('CXP CRL Filter Toggled', {
      requestId: this.display()?.requestId ?? 0,
      filter: key,
      active: next.has(key),
    });
  }

  /**
   * Clickable status tile (PP-3642). Tiles toggle the SAME filter buckets as the
   * Filters menu — so a tile click is just an `onFilterToggled` with the tile's
   * mapped key, composing through the one selection mechanism.
   */
  onTileToggled(key: CrlFilterKey): void {
    this.onFilterToggled(key);
  }

  onSearchChanged(query: string): void {
    this.searchQuery.set(query);
  }

  /** Scope the list to one section (or null = All Sections). */
  onSectionChanged(sectionId: CrlSectionFilter): void {
    this.sectionFilter.set(sectionId);
    this.trackService.track('CXP CRL Section Scope Changed', {
      requestId: this.display()?.requestId ?? 0,
      sectionId: sectionId ?? 0,
    });
  }

  /**
   * REQ-11 — enter/exit bulk-actions selection mode. Exiting always clears the
   * current selection so re-entering starts clean.
   */
  onBulkModeToggled(): void {
    const next = !this.bulkMode();
    this.bulkMode.set(next);
    if (!next) this.selectedItemIds.set(new Set());
    this.trackService.track('CXP CRL Bulk Mode Toggled', {
      requestId: this.display()?.requestId ?? 0,
      active: next,
    });
  }

  /** Toggle a single row's selection in bulk mode (restricted rows are ignored). */
  onItemSelectionToggled(item: CrlRequestItem): void {
    if (!isCrlItemBulkSelectable(item)) return;
    const next = new Set(this.selectedItemIds());
    if (next.has(item.id)) {
      next.delete(item.id);
    } else {
      next.add(item.id);
    }
    this.selectedItemIds.set(next);
  }

  /**
   * REQ-11 SHELL — batch action chosen from the toolbar. Intentionally NOT wired
   * to any mutation yet; this only records intent. TODO(PP-3642 follow-up): wire
   * Mark Finished / Mark N/A / Reassign to real bulk mutations through the
   * facade once the backend batch endpoints land.
   */
  onBulkAction(action: CrlBulkAction): void {
    this.logger.info('[CxpNewCrlPage] bulk action requested (no-op shell):', action, [...this.selectedItemIds()]);
    this.trackService.track('CXP CRL Bulk Action Requested', {
      requestId: this.display()?.requestId ?? 0,
      action,
      count: this.selectedItemIds().size,
    });
  }

  /** Exit selection mode from the batch toolbar's Done control. */
  onBulkDone(): void {
    this.bulkMode.set(false);
    this.selectedItemIds.set(new Set());
  }

  /** Clear every active filter (search is intentionally preserved). */
  onClearAllFilters(): void {
    this.commitSelection(new Set());
    this.trackService.track('CXP CRL Filters Cleared', { requestId: this.display()?.requestId ?? 0 });
  }

  /**
   * The action banner's CTA — toggles the overdue + needs-update preset, so the
   * same button that narrows the list to the items it warns about also lifts that
   * filter again. Lifting removes only the preset's own buckets, leaving any
   * filter the user set elsewhere (a tile, the Filters menu) in place.
   */
  onFilterRequested(): void {
    const next = toggleCrlPreset(this.selection(), CRL_ACTION_REQUIRED_FILTERS);
    this.commitSelection(next);
    this.trackService.track('CXP CRL Action Banner Filter Clicked', {
      requestId: this.display()?.requestId ?? 0,
      active: isCrlPresetActive(next, CRL_ACTION_REQUIRED_FILTERS),
    });
  }

  /** Single write path: update the signal + persist via the filters service. */
  private commitSelection(selection: CrlFilterSelection): void {
    this.selection.set(selection);
    this.filtersService.saveSelection(selection);
  }
}
